import { Fragment, useCallback, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, useApi, type Bundle, type BundleFile } from "../lib/api.ts";
import { useAsync } from "../lib/useAsync.ts";
import { formatBytes, formatRelative } from "../lib/format.ts";
import { skillHref, unitLabel, useSkillKey } from "../lib/skillKey.ts";
import { CopyField } from "../components/CopyField.tsx";
import { ErrorState, Spinner } from "../components/States.tsx";

/** Pick the file to show on the plate: SKILL.md, else the first markdown file. */
export function primaryMarkdown(files: BundleFile[]): BundleFile | undefined {
  return (
    files.find((f) => f.path === "SKILL.md") ??
    files.find((f) => /\.(md|markdown)$/i.test(f.path))
  );
}

export default function SkillDetail() {
  const api = useApi();
  const key = useSkillKey();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const fetcher = useCallback(
    async (signal: AbortSignal) => {
      const [bundle, history] = await Promise.all([
        api.getSkill(key, undefined, signal),
        api.getSkillHistory(key, signal),
      ]);
      const md = primaryMarkdown(bundle.files);
      const content = md
        ? await api.getSkillFile(key, md.path, bundle.version, signal)
        : null;
      return { bundle, history, md, content };
    },
    // key is derived from the URL; re-fetch when any part changes.
    [api, key.scope, key.agent, key.name, key.project],
  );
  const { data, error, loading, reload } = useAsync(fetcher, [fetcher]);

  const head = data?.bundle.head ?? data?.bundle.version ?? 0;
  const previous = data?.history.find((h) => h.version < head);

  async function rollback() {
    if (!previous || busy) return;
    if (
      !window.confirm(
        `Republish v${previous.version} as v${head + 1}? The current v${head} stays in history.`,
      )
    )
      return;
    setBusy("rollback");
    setActionError(null);
    try {
      await api.rollbackSkill(key, previous.version);
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    if (busy) return;
    if (
      !window.confirm(
        `Delete ${unitLabel(key.name)} and all ${head} version${head === 1 ? "" : "s"} from the server? Members keep their local copies.`,
      )
    )
      return;
    setBusy("delete");
    setActionError(null);
    try {
      await api.deleteSkill(key);
      navigate("/skills");
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  }

  const total = useMemo(
    () => data?.bundle.files.reduce((n, f) => n + f.size, 0) ?? 0,
    [data],
  );

  const description = data?.bundle.skills[0]?.description;

  return (
    <section>
      <div className="page-header">
        <div>
          <p className="page-subtitle" style={{ margin: "0 0 0.45rem" }}>
            <Link to="/skills" className="link">
              Skills
            </Link>{" "}
            / {key.scope} / {key.agent}
            {key.name.includes("/") && ` / ${key.name.split("/").slice(0, -1).join("/")}`}
          </p>
          <h1 className="page-title">{unitLabel(key.name)}</h1>
          {description && <p className="page-subtitle">{description}</p>}
        </div>
        {data && (
          <div className="page-actions">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={remove}
              disabled={busy !== null}
            >
              {busy === "delete" ? "…" : "Delete"}
            </button>
            {previous && (
              <button
                type="button"
                className="btn btn--ghost"
                onClick={rollback}
                disabled={busy !== null}
              >
                {busy === "rollback" ? "…" : `Rollback to v${previous.version}`}
              </button>
            )}
            {data.md && (
              <Link to={skillHref(key, true)} className="btn btn--primary">
                Edit {data.md.path.split("/").pop()}
              </Link>
            )}
          </div>
        )}
      </div>

      {loading && <Spinner />}
      {error && (
        <ErrorState
          error={
            error instanceof ApiError && error.status === 404
              ? new Error("This skill no longer exists on the server.")
              : error
          }
          onRetry={reload}
        />
      )}
      {actionError && (
        <div className="warning-banner" style={{ marginBottom: "1.25rem" }}>
          <strong>That didn&apos;t work.</strong> {actionError}
        </div>
      )}

      {data && (
        <div className="detail-grid">
          <div>
            {data.md && data.content !== null && (
              <>
                <div className="plate-head">
                  <span>
                    {data.md.path} · {formatBytes(data.md.size)}
                  </span>
                  <span>v{data.bundle.version}</span>
                </div>
                <pre className="plate">{highlightMarkdown(data.content)}</pre>
              </>
            )}

            <table className="table" style={{ marginTop: data.md ? "1.5rem" : 0 }}>
              <thead>
                <tr>
                  <th>File</th>
                  <th className="num">Size</th>
                  <th className="num">Mode</th>
                </tr>
              </thead>
              <tbody>
                {data.bundle.files.map((f) => (
                  <tr key={f.path}>
                    <td className="mono">{f.path}</td>
                    <td className="num mono dim">{formatBytes(f.size)}</td>
                    <td className="num mono dim">{modeString(f.mode)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            <div className="card">
              <span className="card-eyebrow">Bundle</span>
              <dl className="kv">
                <dt>Scope</dt>
                <dd>
                  <span className={key.scope === "org" ? "badge badge--admin" : "badge"}>
                    {key.scope}
                  </span>
                </dd>
                <dt>Agent</dt>
                <dd>
                  <span className="badge badge--agent">{key.agent}</span>
                </dd>
                {key.project && (
                  <>
                    <dt>Project</dt>
                    <dd className="mono ellipsis" title={key.project}>
                      {key.project}
                    </dd>
                  </>
                )}
                <dt>Head</dt>
                <dd className="mono">
                  v{head} · {data.bundle.files.length} file
                  {data.bundle.files.length === 1 ? "" : "s"} · {formatBytes(total)}
                </dd>
                <dt>Author</dt>
                <dd className="mono">
                  {data.bundle.author || "?"} · {data.bundle.host || "?"}
                </dd>
              </dl>
            </div>

            <div className="card">
              <span className="card-eyebrow">History</span>
              <ul className="hist">
                {data.history.map((h: Bundle) => (
                  <li key={h.version}>
                    <span className={h.version === head ? "v v--head" : "v"}>
                      v{h.version}
                    </span>
                    <span className="who">
                      {h.author || "?"} · {h.host || "?"}
                    </span>
                    <span className="dim" title={h.created}>
                      {formatRelative(h.created)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <CopyField
              label="Pull on a machine"
              value={pullCommand(key.scope)}
              prompt="$"
              multiline
            />
          </div>
        </div>
      )}
    </section>
  );
}

function pullCommand(scope: string): string {
  return scope === "org" ? "stift pull --skills --scope org" : "stift pull --skills";
}

function modeString(mode: number): string {
  // Only the exec bit is honoured by the engine; render the usual pair.
  return mode & 0o111 ? "755" : "644";
}

/**
 * Very small line-based highlighter matching the design plate: frontmatter
 * fences + keys, and headings. Everything else is plain text.
 */
function highlightMarkdown(src: string) {
  const lines = src.replace(/\r\n?/g, "\n").split("\n");
  let inFront = false;
  let fenceSeen = 0;
  return lines.map((line, i) => {
    let node: ReactNode = line;
    if (line === "---" && fenceSeen < 2 && (i === 0 || inFront)) {
      inFront = !inFront;
      fenceSeen++;
      node = <span className="fm">---</span>;
    } else if (inFront) {
      const m = /^([A-Za-z0-9_-]+:)(.*)$/.exec(line);
      node = m ? (
        <>
          <span className="fm-key">{m[1]}</span>
          {m[2]}
        </>
      ) : (
        line
      );
    } else if (/^#{1,6}\s/.test(line)) {
      node = <span className="h">{line}</span>;
    }
    return (
      <Fragment key={i}>
        {node}
        {i < lines.length - 1 ? "\n" : null}
      </Fragment>
    );
  });
}
